import { useCallback, useEffect, useRef, useState } from "react";
import { ARCHITECTS_MODS, CURSEFORGE_API_KEY } from "../config/architectsMods";

const POLL_INTERVAL_MS = 60_000;
const MAX_ANIMATED_INCREMENT = 120;
const MAX_ANIMATION_MS = 900;
const CACHE_KEY = "architects-mod-download-counts-v1";

async function fetchFreshJson(url, options = {}) {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 15_000);
  try {
    const response = await fetch(url, {
      ...options,
      cache: "no-store",
      credentials: "omit",
      signal: controller.signal,
    });
    if (!response.ok) throw new Error(`Download request returned HTTP ${response.status}`);
    return await response.json();
  } finally {
    window.clearTimeout(timeout);
  }
}

function readCachedCounts() {
  try {
    const value = JSON.parse(window.localStorage.getItem(CACHE_KEY) || "{}");
    return value && typeof value === "object" ? value : {};
  } catch (_) {
    return {};
  }
}

async function fetchDownloadCounts(previousCounts) {
  const requests = [];
  const curseforgeIds = ARCHITECTS_MODS.map(({ curseforgeId }) => curseforgeId).filter(Boolean);
  if (curseforgeIds.length && CURSEFORGE_API_KEY) {
    requests.push(async () => {
      const payload = await fetchFreshJson("https://api.curseforge.com/v1/mods", {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-api-key": CURSEFORGE_API_KEY },
        body: JSON.stringify({ modIds: curseforgeIds }),
      });
      const projects = payload?.data || [];
      return Object.fromEntries(projects.map(({ id, downloadCount }) => [`curseforge:${id}`, downloadCount]));
    });
  }

  ARCHITECTS_MODS.forEach(({ modrinthId }) => {
    if (modrinthId) {
      requests.push(async () => {
        // OBS persists its HTTP cache across launches. Bust the API URL itself,
        // not just the overlay page, while still polling only once per minute.
        const minute = Math.floor(Date.now() / POLL_INTERVAL_MS);
        const payload = await fetchFreshJson(`https://api.modrinth.com/v2/project/${encodeURIComponent(modrinthId)}?overlay_refresh=${minute}`);
        return { [`modrinth:${modrinthId}`]: payload?.downloads };
      });
    }
  });

  const settled = await Promise.allSettled(requests.map((run) => run()));
  const configuredKeys = new Set([
    ...curseforgeIds.map((id) => `curseforge:${id}`),
    ...ARCHITECTS_MODS.map(({ modrinthId }) => modrinthId && `modrinth:${modrinthId}`).filter(Boolean),
  ]);
  const counts = Object.fromEntries(
    Object.entries(previousCounts).filter(([key]) => configuredKeys.has(key)),
  );
  settled.forEach((result) => {
    if (result.status !== "fulfilled") {
      console.warn("Architects downloads: a platform request failed; retaining its last valid count.");
      return;
    }
    Object.entries(result.value).forEach(([key, count]) => {
      if (configuredKeys.has(key) && Number.isSafeInteger(count) && count >= 0) counts[key] = count;
    });
  });
  try {
    window.localStorage.setItem(CACHE_KEY, JSON.stringify(counts));
  } catch (_) {
    // Storage restrictions must not discard successfully fetched totals.
  }
  return counts;
}

export default function ArchitectsDownloadsOverlay() {
  const [downloads, setDownloads] = useState(null);
  const displayedRef = useRef(null);
  const animationRef = useRef(null);

  useEffect(() => {
    const previous = {
      html: document.documentElement.style.background,
      body: document.body.style.background,
      root: document.getElementById("root")?.style.background || "",
    };
    document.documentElement.style.background = "transparent";
    document.body.style.background = "transparent";
    if (document.getElementById("root")) document.getElementById("root").style.background = "transparent";
    return () => {
      document.documentElement.style.background = previous.html;
      document.body.style.background = previous.body;
      if (document.getElementById("root")) document.getElementById("root").style.background = previous.root;
    };
  }, []);

  const showTotal = useCallback((nextTotal) => {
    const previousTotal = displayedRef.current;
    window.cancelAnimationFrame(animationRef.current);

    if (
      previousTotal === null ||
      nextTotal <= previousTotal ||
      nextTotal - previousTotal > MAX_ANIMATED_INCREMENT ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      displayedRef.current = nextTotal;
      setDownloads(nextTotal);
      return;
    }

    const difference = nextTotal - previousTotal;
    const stepMs = Math.min(45, MAX_ANIMATION_MS / difference);
    let shown = previousTotal;
    let lastStep = performance.now();
    const tick = (now) => {
      if (now - lastStep >= stepMs) {
        const steps = Math.max(1, Math.floor((now - lastStep) / stepMs));
        shown = Math.min(nextTotal, shown + steps);
        lastStep = now;
        displayedRef.current = shown;
        setDownloads(shown);
      }
      if (shown < nextTotal) animationRef.current = window.requestAnimationFrame(tick);
    };
    animationRef.current = window.requestAnimationFrame(tick);
  }, []);

  useEffect(() => {
    let active = true;
    const refresh = async () => {
      try {
        const counts = await fetchDownloadCounts(readCachedCounts());
        const values = Object.values(counts).filter((value) => Number.isSafeInteger(value) && value >= 0);
        if (active && values.length) showTotal(values.reduce((total, value) => total + value, 0));
      } catch (_) {
        // OBS keeps showing its last valid total without surfacing API errors.
      }
    };
    refresh();
    const timer = window.setInterval(refresh, POLL_INTERVAL_MS);
    return () => {
      active = false;
      window.clearInterval(timer);
      window.cancelAnimationFrame(animationRef.current);
    };
  }, [showTotal]);

  return (
    <main className="architects-downloads-overlay" aria-live="polite">
      {downloads !== null && (
        <div>
          <div className="architects-downloads-label">Architects Downloads</div>
          <div className="architects-downloads-number">{downloads.toLocaleString("en-US")}</div>
        </div>
      )}
    </main>
  );
}
